import type {
  CheckboxRuleValidationSchema,
  FormFieldType
} from '@thedealersconcierge/lib/codecs/schema/formField';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import range from 'lodash/range';
import { ChangeEvent, Fragment, useMemo, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import SignatureInput from '../input/signatureInput';
// Consumers must ensure the worker is available
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

export type PdfPrefillValue =
  | {
      t: 'checkbox';
      value?: boolean;
    }
  | {
      t: 'string';
      value: string;
    }
  | {
      t: 'file';
      fileUrl: string;
      fileId: string;
    };

export type PdfFormFieldPlacement = {
  formFieldPlacementId: string;
  page: number;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  /**
   * Required for TEXT fields.
   * TEXT fields cannot render without a font size, so this value must be present
   * whenever the owning PdfFormField.type === 'TEXT'.
   * It remains optional at the type level because non-TEXT fields do not use it.
   */
  textFontSize?: number | null;
  /**
   * Required for TEXT fields.
   * Controls horizontal alignment of text.
   * Optional for all other field types because they do not render text.
   */
  textAlignment?: 'LEFT' | 'CENTER' | 'RIGHT' | null;
  /**
   * Controls the spacing between character boxes in CHARACTER_LIST fields.
   * Only used when the field type is CHARACTER_LIST.
   * Other field types do not display character boxes, so this can be empty for them.
   */
  characterListSpacing?: number | null;
};

export type PdfFormField = {
  type: FormFieldType;
  /**
   * Required for CHARACTER_LIST fields.
   * Specifies the fixed length of the character list.
   * Optional for all other field types which do not rely on fixed-length input boxes.
   */
  characterListLength?: number | null;

  checkboxGroup?: {
    id: string;
    rule: CheckboxRuleValidationSchema['rule'];
    /**
     * Required when rule === 'EXACTLY_N'.
     * Optional for all other rules that do not depend on a numeric constraint.
     */
    n?: number | null;
  };
  formFieldId: string;
  formFieldPlacements: PdfFormFieldPlacement[];
};

type Props = {
  authHeaders: HeadersInit;
  pdfUrl: string;
  formFields: PdfFormField[];
  /**
   * Read the current prefill value for a given logical formFieldId
   */
  getValue: (formFieldId: string) => PdfPrefillValue | undefined;
  /**
   * Update the current prefill value for a given logical formFieldId
   */
  setValue: (formFieldId: string, value: PdfPrefillValue) => void;
  /**
   * Upload a signature image and return the resulting file metadata
   */
  onUploadSignature: (
    blob: Blob
  ) => Promise<{ fileId: string; fileUrl: string }>;
  /**
   * Get's called with the width of the widest page in the document
   */
  onUpdateMaxPageWidth: (max: number) => void;
};

export default function PdfFilling({
  authHeaders,
  getValue,
  setValue,
  onUploadSignature,
  pdfUrl,
  formFields,
  onUpdateMaxPageWidth
}: Props) {
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());
  const divRefs = useRef<Map<string, HTMLElement>>(new Map());
  const [numPages, setNumPages] = useState(0);

  // Collect widths without re-renders
  const widthsRef = useRef<number[]>([]);
  const debouncedMaxWidth = useRef(
    debounce(() => {
      const max = Math.max(...widthsRef.current);
      onUpdateMaxPageWidth(max);
    }, 200)
  ).current;

  function onDocumentLoadSuccess({ numPages }: { numPages: number }): void {
    setNumPages(numPages);
  }

  const updateValue = (formFieldId: string, value: PdfPrefillValue) => {
    setValue(formFieldId, value);

    // Update refs for instant UI feedback
    const field = formFields?.find((ffs) => ffs.formFieldId === formFieldId);
    field?.formFieldPlacements?.forEach((ffp) => {
      const key = ffp.formFieldPlacementId ?? 'no-form-field-placement-id';
      if (value.t === 'string') {
        const input = inputRefs.current.get(key);
        if (input) {
          input.value = value.value;
        }
      } else if (value.t === 'checkbox') {
        const input = inputRefs.current.get(key);
        if (input) {
          input.checked = value.value ?? false;
        }
      } else if (value.t === 'file') {
        const div = divRefs.current.get(key);
        if (div?.innerHTML !== undefined) {
          div.innerHTML = `<img src="${value.fileUrl}" />`;
        }
      }
    });
  };

  const setSignature = async (formFieldId: string, dataURI: string) => {
    const blob = await (await fetch(dataURI)).blob();
    const uploadInfo = await onUploadSignature(blob);
    updateValue(formFieldId, {
      t: 'file',
      fileId: uploadInfo.fileId,
      fileUrl: uploadInfo.fileUrl ?? dataURI
    });
  };

  return (
    <div>
      <Document
        options={useMemo(
          () => ({ httpHeaders: authHeaders }),
          [JSON.stringify(authHeaders)]
        )}
        file={pdfUrl}
        onLoadSuccess={onDocumentLoadSuccess}
        className="flex flex-col space-y-1"
        loading={
          <div
            className="flex w-full h-72 justify-center items-center"
            data-test-id={'pdf-filling-document-spinner'}
          >
            {/* Consumers should provide a Spinner in their app if desired */}
            Loading document...
          </div>
        }
        error={
          <div className="flex flex-col w-full h-72 justify-center items-center">
            Failed to load document
          </div>
        }
      >
        {range(numPages).map((idx, pageNumberMinusOne) => (
          <div data-test-id={'pdf-filling-page-wrapper'} key={idx}>
            <Page
              key={pageNumberMinusOne}
              pageNumber={pageNumberMinusOne + 1}
              renderAnnotationLayer={false}
              renderTextLayer={false}
              className="border border-inactive"
              onLoadSuccess={({ originalWidth }: { originalWidth: number }) => {
                widthsRef.current[idx] = originalWidth;
                debouncedMaxWidth();
              }}
            >
              {formFields.map((field, i) => (
                <Fragment key={field.formFieldId ?? `field-${i}`}>
                  {field.formFieldPlacements
                    ?.filter((p) => p.page === pageNumberMinusOne)
                    .map((placement) => {
                      const initialValue = field.formFieldId
                        ? getValue(field.formFieldId)
                        : undefined;
                      const inputKey = `${field.formFieldId ?? 'no-field'}-${placement.formFieldPlacementId ?? 'no-placement'}-${pageNumberMinusOne}`;

                      if (field.type === 'TEXT') {
                        return (
                          <input
                            defaultValue={
                              initialValue?.t === 'string'
                                ? initialValue.value
                                : ''
                            }
                            key={inputKey}
                            ref={(el) => {
                              const key =
                                placement.formFieldPlacementId ??
                                'no-form-field-placement-id';
                              if (el) {
                                inputRefs.current.set(key, el);
                              } else {
                                inputRefs.current.delete(key);
                              }
                            }}
                            name={placement.formFieldPlacementId ?? undefined}
                            className={classNames(
                              'border-yellow-500 border-2 absolute bg-transparent align-top p-0 m-0 overflow-hidden text-black',
                              {
                                'text-left': placement.textAlignment === 'LEFT',
                                'text-center':
                                  placement.textAlignment === 'CENTER',
                                'text-right':
                                  placement.textAlignment === 'RIGHT'
                              }
                            )}
                            style={{
                              resize: 'none',
                              boxSizing: 'border-box',
                              fontSize: placement.textFontSize ?? undefined,
                              left: placement.positionX ?? undefined,
                              top: placement.positionY ?? undefined,
                              width: placement.width ?? undefined,
                              height: placement.height ?? undefined
                            }}
                            data-test-id={`form-pdf-filling-${field.type}-${placement.formFieldPlacementId}`}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              if (field.formFieldId) {
                                updateValue(field.formFieldId, {
                                  t: 'string',
                                  value: e.target.value
                                });
                              }
                            }}
                          />
                        );
                      } else if (field.type === 'CHARACTER_LIST') {
                        return (
                          <input
                            defaultValue={
                              initialValue?.t === 'string'
                                ? initialValue.value
                                : ''
                            }
                            key={inputKey}
                            ref={(el) => {
                              const key =
                                placement.formFieldPlacementId ??
                                'no-form-field-placement-id';
                              if (el) {
                                inputRefs.current.set(key, el);
                              } else {
                                inputRefs.current.delete(key);
                              }
                            }}
                            name={placement.formFieldPlacementId ?? undefined}
                            className="border-yellow-500 border-2 absolute bg-transparent align-top p-0 m-0 overflow-hidden text-black"
                            maxLength={field.characterListLength ?? undefined}
                            style={{
                              resize: 'none',
                              boxSizing: 'border-box',
                              fontSize: placement.textFontSize ?? undefined,
                              left: placement.positionX ?? undefined,
                              letterSpacing: placement.width ?? undefined,
                              top: placement.positionY ?? undefined,
                              width:
                                (placement.width ?? 0) *
                                  (field.characterListLength ?? 1) +
                                (placement.width ?? 0) +
                                // Space for the cursor to not push the text to the left
                                2 * (placement.width ?? 0),
                              padding: (placement.width ?? 0) / 2,
                              height: placement.height ?? undefined
                            }}
                            data-test-id={`form-pdf-filling-${field.type}-${placement.formFieldPlacementId}`}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              if (field.formFieldId) {
                                updateValue(field.formFieldId, {
                                  t: 'string',
                                  value: e.target.value
                                });
                              }
                            }}
                          />
                        );
                      } else if (field.type === 'CHECKBOX') {
                        return (
                          <input
                            type="checkbox"
                            defaultChecked={
                              initialValue?.t === 'checkbox'
                                ? Boolean(initialValue.value)
                                : false
                            }
                            key={inputKey}
                            ref={(el) => {
                              const key =
                                placement.formFieldPlacementId ??
                                'no-form-field-placement-id';
                              if (el) {
                                inputRefs.current.set(key, el);

                                if (
                                  field.formFieldId &&
                                  initialValue === undefined
                                ) {
                                  setValue(field.formFieldId, {
                                    t: 'checkbox',
                                    value: false
                                  });
                                }
                              } else {
                                inputRefs.current.delete(key);
                              }
                            }}
                            name={placement.formFieldPlacementId ?? undefined}
                            className="border-yellow-500 border-2 absolute bg-transparent align-top p-0 m-0 overflow-hidden"
                            style={{
                              resize: 'none',
                              boxSizing: 'border-box',
                              fontSize: placement.textFontSize ?? undefined,
                              left: placement.positionX ?? undefined,
                              top: placement.positionY ?? undefined,
                              width: placement.width ?? undefined,
                              height: placement.height ?? undefined
                            }}
                            data-test-id={`form-pdf-filling-${field.type}-${placement.formFieldPlacementId}`}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => {
                              if (field.formFieldId) {
                                updateValue(field.formFieldId, {
                                  t: 'checkbox',
                                  value: e.target.checked
                                });
                              }
                            }}
                          />
                        );
                      } else if (field.type === 'SIGNATURE') {
                        return (
                          <div
                            ref={(el) => {
                              const key =
                                placement.formFieldPlacementId ??
                                'no-form-field-placement-id';
                              if (el) {
                                divRefs.current.set(key, el);
                              } else {
                                divRefs.current.delete(key);
                              }
                            }}
                            key={inputKey}
                            className="relative cursor-pointer"
                            style={{
                              position: 'absolute',
                              left: placement.positionX ?? undefined,
                              top: placement.positionY ?? undefined,
                              width: placement.width ?? undefined,
                              height: placement.height ?? undefined
                            }}
                            data-test-id={`form-pdf-filling-${field.type}-${placement.formFieldPlacementId}`}
                          >
                            <SignatureInput
                              translations={{
                                cancelButton: 'Cancel',
                                submitButton: 'Submit',
                                modalTitle: 'Signature',
                                signature: 'Signature',
                                tabToSign: 'Tap here to sign'
                              }}
                              required={false}
                              disabled={false}
                              context="SIGNING_CEREMONY"
                              template={{
                                width: placement.width ?? 150,
                                height: placement.height ?? 60
                              }}
                              value={null}
                              onSubmit={(signature) => {
                                if (field.formFieldId) {
                                  void setSignature(
                                    field.formFieldId,
                                    signature
                                  );
                                }
                              }}
                              dataTestIdCanvas={`form-pdf-signature-canvas-${placement.formFieldPlacementId}`}
                              dataTestIdOpenModal={`form-pdf-open-modal-${placement.formFieldPlacementId}`}
                              dataTestIdSubmit={`form-pdf-submit-button-${placement.formFieldPlacementId}`}
                              dataTestIdPreview={`form-pdf-signature-preview-${placement.formFieldPlacementId}`}
                            />
                          </div>
                        );
                      }
                    })}
                </Fragment>
              ))}
            </Page>
          </div>
        ))}
      </Document>
    </div>
  );
}
